import { useCallback, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useUploadResume } from '@/hooks/useUploadResume';
import { useFolderUpload } from '@/hooks/useFolderUpload';
import { CandidateSummary } from '@recruitai/shared';

interface ResumeUploadZoneProps {
  jobId: string;
  onUploadComplete: (candidates: CandidateSummary[]) => void;
  onCancel?: () => void;
}

export function ResumeUploadZone({ jobId, onUploadComplete, onCancel }: ResumeUploadZoneProps) {
  const { files, uploading, error, addFiles, removeFile, uploadFiles, retryFile, clearFiles } = useUploadResume(jobId);
  const folder = useFolderUpload(jobId);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [addFiles]);

  const handleUpload = async () => {
    const result = await uploadFiles();
    if (result.length > 0) {
      onUploadComplete(result);
      clearFiles();
    }
  };

  const handleFolderUpload = async () => {
    const result = await folder.startUpload();
    const errors = folder.folderFiles.filter(f => f.status === 'error').length;
    if (result.length > 0 && errors === 0) {
      toast.success(`${result.length} currículo(s) enviado(s) com sucesso`);
      onUploadComplete(result);
      folder.clearFiles();
    } else if (result.length > 0 && errors > 0) {
      toast.warning(`${result.length} currículo(s) enviado(s). ${errors} falha(s) no envio.`);
      onUploadComplete(result);
    }
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const folderValidCount = folder.folderFiles.filter(f => f.status === 'valid' || f.status === 'pending').length;
  const hasFolderFiles = folder.folderFiles.length > 0;
  const progressPct = folder.overallProgress.total > 0
    ? Math.round((folder.overallProgress.completed / folder.overallProgress.total) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Drop zone + folder button */}
      {!hasFolderFiles && (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx" onChange={handleFileSelect} className="hidden" />
            <div className="text-4xl mb-2">📄</div>
            <p className="text-sm font-medium">Arraste currículos aqui ou clique para selecionar</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX • Máx. 10MB por arquivo • Até 20 arquivos</p>
          </div>

          <div className="flex justify-center">
            <Button variant="outline" onClick={folder.selectFolder} disabled={folder.isSelecting || uploading}>
              📁 Selecionar Pasta
            </Button>
            <input
              ref={folder.folderInputRef}
              type="file"
              {...({ webkitdirectory: '', directory: '' } as any)}
              multiple
              onChange={folder.handleFolderInput}
              className="hidden"
            />
          </div>
        </>
      )}

      {/* Individual file list */}
      {files.length > 0 && !hasFolderFiles && (
        <div className="space-y-2">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm">
              <span className="flex-1 truncate">{f.file.name}</span>
              <span className="text-xs text-muted-foreground">{(f.file.size / 1024 / 1024).toFixed(1)}MB</span>
              {f.status === 'pending' && (
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}>✕</Button>
              )}
              {f.status === 'uploading' && <span className="text-xs text-blue-600">{f.progress}%</span>}
              {f.status === 'uploaded' && <span className="text-xs text-green-600">✓</span>}
              {f.status === 'error' && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-red-600">{f.error}</span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); retryFile(f.id); }}>↻</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Folder file list */}
      {hasFolderFiles && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">📁 {folder.folderFiles.length} arquivo(s) encontrado(s)</p>
            <Button variant="ghost" size="sm" onClick={folder.clearFiles} disabled={folder.isUploading}>✕ Limpar</Button>
          </div>

          {folder.truncatedCount > 0 && (
            <p className="text-xs text-amber-600">Limite de 20 arquivos por lote atingido. {folder.truncatedCount} arquivo(s) foram ignorados.</p>
          )}

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {folder.folderFiles.map(f => (
              <div key={f.id} className={`flex items-center gap-3 p-2 rounded text-sm ${f.status === 'invalid' ? 'bg-red-50' : 'bg-gray-50'}`}>
                <span className="flex-1 truncate">{f.file.name}</span>
                <span className="text-xs text-muted-foreground">{(f.file.size / 1024 / 1024).toFixed(1)}MB</span>
                {f.status === 'invalid' && <span className="text-xs text-red-600">{f.error}</span>}
                {(f.status === 'valid' || f.status === 'pending') && <span className="text-xs text-gray-400">○</span>}
                {f.status === 'uploading' && <span className="text-xs text-blue-600">{f.progress}%</span>}
                {f.status === 'uploaded' && <span className="text-xs text-green-600">✓</span>}
                {f.status === 'error' && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-red-600">{f.error}</span>
                    <Button variant="ghost" size="sm" onClick={() => folder.retryFile(f.id)}>↻</Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Progress bar */}
          {folder.isUploading && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {folder.overallProgress.completed} de {folder.overallProgress.total} arquivos enviados
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}

          {folderValidCount === 0 && !folder.isUploading && (
            <p className="text-sm text-amber-600">Nenhum arquivo válido para envio.</p>
          )}
        </div>
      )}

      {(error || folder.error) && <p className="text-sm text-red-600">{error || folder.error}</p>}

      {/* Actions - individual */}
      {files.length > 0 && !hasFolderFiles && (
        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={onCancel} disabled={uploading}>Cancelar</Button>}
          <Button onClick={handleUpload} disabled={uploading || pendingCount === 0}>
            {uploading ? 'Enviando...' : `Enviar ${pendingCount} currículo${pendingCount !== 1 ? 's' : ''}`}
          </Button>
        </div>
      )}

      {/* Actions - folder */}
      {hasFolderFiles && (
        <div className="flex gap-2 justify-end">
          {folder.isUploading ? (
            <Button variant="outline" onClick={folder.cancelUpload}>Cancelar</Button>
          ) : (
            <>
              {onCancel && <Button variant="outline" onClick={() => { folder.clearFiles(); onCancel?.(); }}>Cancelar</Button>}
              <Button onClick={handleFolderUpload} disabled={folderValidCount === 0}>
                Enviar {folderValidCount} válido{folderValidCount !== 1 ? 's' : ''}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
